import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Edit3, Save, X } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { useUnitFormatters } from '@/hooks/useUnits';
import {
  cmToDisplayHeight,
  displayHeightToCm,
  displayWeightToStoredKg,
  formatHeightDisplay,
  kgToDisplayWeight,
  roundMetricForProfile,
} from '@/lib/units';

export const VitalStatsCard = () => {
  const { t } = useTranslation();
  const { profile, updateProfile } = useProfile();
  const { toast } = useToast();
  const { units, formatWeight, weightLabel } = useUnitFormatters();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    weight_display: '' as string,
    height_cm: 0,
    height_ft: 0,
    height_in: 0,
    gender: profile?.gender || '',
    activity_level: profile?.activity_level || 'moderate',
    birth_date: profile?.birth_date || ''
  });

  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const calculateBMI = (weight: number | null, height: number | null) => {
    if (!weight || !height || height === 0) return null;
    const heightInMeters = height / 100;
    return (weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  const handleEdit = () => {
    const weightKg = profile?.weight_kg || 0;
    const heightCm = profile?.height_cm || 0;
    const { ft = 0, inch = 0 } = heightCm
      ? cmToDisplayHeight(heightCm, 'imperial')
      : { ft: 0, inch: 0 };
    const weightDisplay = weightKg
      ? String(kgToDisplayWeight(weightKg, units))
      : '';
    setEditValues({
      weight_display: weightDisplay,
      height_cm: heightCm,
      height_ft: ft,
      height_in: inch,
      gender: profile?.gender || '',
      activity_level: profile?.activity_level || 'moderate',
      birth_date: profile?.birth_date || ''
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const weightKg = editValues.weight_display
        ? displayWeightToStoredKg(parseFloat(editValues.weight_display), units)
        : null;
      const heightCm = units === 'imperial'
        ? roundMetricForProfile(displayHeightToCm(editValues.height_ft || 0, editValues.height_in || 0))
        : roundMetricForProfile(editValues.height_cm || null);

      const { error } = await updateProfile({
        weight_kg: weightKg,
        height_cm: heightCm,
        gender: editValues.gender,
        activity_level: editValues.activity_level,
        birth_date: editValues.birth_date,
      });
      if (error) throw error;
      
      toast({
        title: t('components.vitalStats.toast.successTitle'),
        description: t('components.vitalStats.toast.successDescription')
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: t('components.vitalStats.toast.errorTitle'),
        description: t('components.vitalStats.toast.errorDescription'),
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const age = calculateAge(profile?.birth_date || null);
  const bmi = calculateBMI(profile?.weight_kg || null, profile?.height_cm || null);

  const activityLevelLabels: Record<string, string> = {
    sedentary: t('components.vitalStats.activityOptions.sedentary'),
    light: t('components.vitalStats.activityOptions.light'),
    moderate: t('components.vitalStats.activityOptions.moderate'),
    active: t('components.vitalStats.activityOptions.active'),
    very_active: t('components.vitalStats.activityOptions.very_active')
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 font-normal">
            <User className="h-5 w-5 text-primary" />
            {t('components.vitalStats.title')}
          </CardTitle>
          {!isEditing ? (
            <Button size="sm" variant="ghost" onClick={handleEdit}>
              <Edit3 className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('components.vitalStats.weight')}</span>
              <span className="text-sm font-medium">
                {profile?.weight_kg ? formatWeight(profile.weight_kg) : t('components.vitalStats.notSet')}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('components.vitalStats.height')}</span>
              <span className="text-sm font-medium">
                {profile?.height_cm ? formatHeightDisplay(profile.height_cm, units) : t('components.vitalStats.notSet')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('components.vitalStats.age')}</span>
              <span className="text-sm font-medium">
                {age ? `${age} ${t('components.vitalStats.yearsSuffix')}` : t('components.vitalStats.notSet')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('components.vitalStats.gender')}</span>
              <span className="text-sm font-medium">
                {profile?.gender
                  ? t(`components.vitalStats.genderOptions.${profile.gender}`, {
                      defaultValue: profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1),
                    })
                  : t('components.vitalStats.notSet')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('components.vitalStats.bmi')}</span>
              <span className="text-sm font-medium">
                {bmi || t('components.vitalStats.notAvailable')}
              </span>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('components.vitalStats.activityLevel')}</span>
                <span className="text-sm font-medium">
                  {profile?.activity_level
                    ? activityLevelLabels[profile.activity_level]
                    : t('components.vitalStats.notSet')}
                </span>
              </div>
            </div>

            {(!profile?.weight_kg || !profile?.height_cm || !profile?.birth_date || !profile?.gender) && (
              <div className="pt-2 text-xs text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
                {t('components.vitalStats.completeProfileHint')}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="weight">{t('components.vitalStats.weight')} ({weightLabel})</Label>
              <Input
                id="weight"
                type="text"
                inputMode="decimal"
                min="1"
                max="300"
                value={editValues.weight_display}
                onChange={(e) => {
                  const value = e.target.value.replace(',', '.');
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setEditValues({ ...editValues, weight_display: value });
                  }
                }}
              />
            </div>

            {units === 'imperial' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="height_ft">{t('components.vitalStats.height')} (ft)</Label>
                  <Input
                    id="height_ft"
                    type="number"
                    min="3"
                    max="8"
                    value={editValues.height_ft || ''}
                    onChange={(e) => setEditValues({ ...editValues, height_ft: parseInt(e.target.value) || 0 })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height_in">{t('components.vitalStats.height')} (in)</Label>
                  <Input
                    id="height_in"
                    type="number"
                    min="0"
                    max="11"
                    value={editValues.height_in || ''}
                    onChange={(e) => setEditValues({ ...editValues, height_in: parseInt(e.target.value) || 0 })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="height">{t('components.vitalStats.height')} (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  min="1"
                  max="300"
                  value={editValues.height_cm || ''}
                  onChange={(e) => setEditValues({ ...editValues, height_cm: parseFloat(e.target.value) || 0 })}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="birth_date">{t('components.vitalStats.birthDateLabel')}</Label>
              <Input
                id="birth_date"
                type="date"
                value={editValues.birth_date || ''}
                onChange={(e) => setEditValues({ ...editValues, birth_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">{t('components.vitalStats.gender')}</Label>
              <Select
                value={editValues.gender}
                onValueChange={(value) => setEditValues({ ...editValues, gender: value })}
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder={t('components.vitalStats.selectGender')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('components.vitalStats.genderOptions.male')}</SelectItem>
                  <SelectItem value="female">{t('components.vitalStats.genderOptions.female')}</SelectItem>
                  <SelectItem value="other">{t('components.vitalStats.genderOptions.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity">{t('components.vitalStats.activityLevel')}</Label>
              <Select
                value={editValues.activity_level}
                onValueChange={(value) => setEditValues({ ...editValues, activity_level: value })}
              >
                <SelectTrigger id="activity">
                  <SelectValue placeholder={t('components.vitalStats.selectActivityLevel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">{t('components.vitalStats.activityOptions.sedentary')}</SelectItem>
                  <SelectItem value="light">{t('components.vitalStats.activityOptions.light')}</SelectItem>
                  <SelectItem value="moderate">{t('components.vitalStats.activityOptions.moderate')}</SelectItem>
                  <SelectItem value="active">{t('components.vitalStats.activityOptions.active')}</SelectItem>
                  <SelectItem value="very_active">{t('components.vitalStats.activityOptions.very_active')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
